// Batch 4 Sections A + B — Taxonomy API client helpers.
// Thin axios wrappers over the read-only taxonomy endpoints so the
// new cascading Preferences UI can fetch dropdown content without
// repeating URL strings + cache keys in every component.
//
// All endpoints are GET, cache-friendly (server returns
// `cached_until_ts`). We let TanStack-Query handle client-side cache.
import { apiClient as api } from "./client";

// ---- Auto taxonomy --------------------------------------------------------
export async function getAutoBrands(country = "AT") {
  const { data } = await api.get("/taxonomy/auto/brands", {
    params: { country },
  });
  return data; // { items: [{brand_canonical, brand_key, country_origin}], count }
}

export async function getAutoModels(brandKey, country = "AT") {
  if (!brandKey) return { items: [], count: 0 };
  const { data } = await api.get("/taxonomy/auto/models", {
    params: { brand: brandKey, country },
  });
  return data; // { brand_canonical, items: [{model_family, source}], count }
}

export async function getAutoSeries(brandKey, modelFamily, country = "AT") {
  if (!brandKey || !modelFamily) return { items: [], count: 0 };
  const { data } = await api.get("/taxonomy/auto/series", {
    params: { brand: brandKey, model: modelFamily, country },
  });
  return data;
}

export async function getAutoFuels() {
  const { data } = await api.get("/taxonomy/auto/fuels");
  return data; // { items: [{slug, label, db_values}] }
}

export async function getAutoGearboxes() {
  const { data } = await api.get("/taxonomy/auto/gearboxes");
  return data;
}

export async function getAutoBodyTypes() {
  const { data } = await api.get("/taxonomy/auto/body-types");
  return data;
}

// ---- Immo taxonomy (Batch 3 canonical endpoints) -------------------------
export async function getImmoTransactionTypes() {
  const { data } = await api.get("/taxonomy/immo/transaction-types");
  return data;
}

export async function getImmoRegions(country = "AT") {
  const { data } = await api.get("/taxonomy/immo/regions", {
    params: { country },
  });
  return data; // { country, items: [{code, display_de, display_en}], count }
}

export async function getImmoDistricts(country = "AT", state = null) {
  const params = { country };
  if (state) params.state = state;
  const { data } = await api.get("/taxonomy/immo/districts", { params });
  return data;
}

export async function getImmoCitiesCanonical(country = "AT", state = null, q = null) {
  const params = { country };
  if (state) params.state = state;
  if (q) params.q = q;
  const { data } = await api.get("/taxonomy/immo/cities-canonical", { params });
  return data;
}

// V4.23 Phase 3a+ — canonical place → bbox/center of live hits.
// Shared resolver with Mobile so both fly to the same place.
export async function locatePlace(q, country = null, vertical = "immo") {
  const params = { q, vertical };
  if (country) params.country = country;
  const { data } = await api.get("/taxonomy/places/locate", { params });
  return data;
}

// V4.23 Phase 3a — dynamic place suggestions for the Heatmap region chip.
export async function searchPlaces(q, country = null) {
  const params = { q, country: country || "AT" };
  const { data } = await api.get("/taxonomy/immo/cities-canonical", { params });
  return data?.items || [];
}

export async function getImmoPostalCodes(country = "AT", state = null) {
  const params = { country };
  if (state) params.state = state;
  const { data } = await api.get("/taxonomy/immo/postal-codes", { params });
  return data;
}

export async function getImmoObjectTypesCanonical(transactionType = null) {
  const params = {};
  if (transactionType) params.transaction_type = transactionType;
  const { data } = await api.get("/taxonomy/immo/object-types-canonical", {
    params,
  });
  return data;
}

// ---- Geo cross-vertical ---------------------------------------------------
export async function getGeoCountries() {
  const { data } = await api.get("/taxonomy/geo/countries");
  return data;
}

// ---- Aggregated filter options (V4.19 — body_types + features) ------------
export async function getAutoFilterOptions(country = "AT") {
  const { data } = await api.get("/filter-options/auto", {
    params: { country },
  });
  return data; // { body_types: [{value}], features: [{value}], ... }
}
